import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    Loader2,
    ArrowRight,
    ArrowLeft,
    Calendar,
    Clock,
    Mail,
    Users,
    UserPlus,
    Video,
    Bell,
    CheckCircle2,
    AlertTriangle,
    PenLine,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VirtualClass } from "@/pages/VirtualClasses";
import { API_URL, getAuthHeader } from "@/config";
import { toast } from "sonner";

interface ValidationWizardProps {
    virtualClass: VirtualClass | null;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    token: string | null;
    onValidated?: (updatedClass: VirtualClass) => void;
    mode?: "validate" | "view";
}

interface PreviewData {
    sample_email: { subject: string; body: string } | null;
}

const formatParisDate = (utcStr: string | null | undefined): string => {
    if (!utcStr) return "—";
    const date = new Date(utcStr);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat("fr-FR", {
        timeZone: "Europe/Paris",
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
};

const ValidationWizard: React.FC<ValidationWizardProps> = ({
    virtualClass,
    isOpen,
    onOpenChange,
    token,
    onValidated,
    mode = "validate",
}) => {
    const totalSteps = mode === "validate" ? 3 : 2;
    const [step, setStep] = useState(1);
    const [isValidating, setIsValidating] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previews, setPreviews] = useState<
        { notification: any; preview: PreviewData }[]
    >([]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setPreviews([]);
        }
    }, [isOpen]);

    const cls = virtualClass;
    const notifications = (cls as any)?.notifications || [];

    // Fetch previews when entering step 2, with proper cleanup for StrictMode
    useEffect(() => {
        if (step !== 2 || previews.length > 0 || !virtualClass || !token) return;
        const notifs = (virtualClass as any)?.notifications || [];
        if (notifs.length === 0) {
            setPreviews([]);
            return;
        }

        const controller = new AbortController();
        let cancelled = false;

        const run = async () => {
            setPreviewLoading(true);
            try {
                const classPayload = {
                    name: virtualClass.name,
                    class_id: virtualClass.id,
                    start_time: virtualClass.start_time || virtualClass.startTime || null,
                    end_time: virtualClass.end_time || virtualClass.endTime || null,
                    url: virtualClass.url || null,
                };

                const settled = await Promise.allSettled(
                    notifs.map(async (notif: any) => {
                        const response = await fetch(`${API_URL}/virtual-classes/preview`, {
                            method: "POST",
                            headers: getAuthHeader(token),
                            signal: controller.signal,
                            body: JSON.stringify({
                                ...classPayload,
                                email_subject: notif.subject,
                                email_body: notif.body,
                            }),
                        });
                        if (!response.ok) return null;
                        const data = await response.json();
                        return { notification: notif, preview: data as PreviewData };
                    })
                );

                if (cancelled) return;

                const results = settled
                    .filter((r): r is PromiseFulfilledResult<{ notification: any; preview: PreviewData } | null> => r.status === "fulfilled")
                    .map((r) => r.value)
                    .filter((v): v is { notification: any; preview: PreviewData } => v !== null);

                setPreviews(results);
                if (results.length === 0) {
                    toast.error("Impossible de charger l'aperçu des emails");
                }
            } catch (error: any) {
                if (cancelled) return;
                if (error.name !== "AbortError") {
                    toast.error("Impossible de charger l'aperçu des emails");
                }
                setPreviews([]);
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        };

        run();

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [step, virtualClass, token]);

    const handleValidate = async () => {
        if (!cls || !token) return;
        setIsValidating(true);
        try {
            const response = await fetch(
                `${API_URL}/virtual-classes/${cls.id}/validate`,
                {
                    method: "POST",
                    headers: getAuthHeader(token),
                }
            );
            if (response.ok) {
                const updatedClass = await response.json();
                toast.success("Classe validée avec succès");
                onValidated?.(updatedClass);
                onOpenChange(false);
            } else {
                const data = await response.json();
                toast.error(data.error || "Erreur lors de la validation");
            }
        } catch (error) {
            toast.error("Erreur lors de la validation");
        } finally {
            setIsValidating(false);
        }
    };

    const formatDuration = (value: number, unit: string): string => {
        let totalMinutes = unit === "hours" ? value * 60 : value;
        if (totalMinutes >= 1440 && totalMinutes % 1440 === 0) {
            const days = totalMinutes / 1440;
            return `${days} jour${days > 1 ? "s" : ""}`;
        }
        if (totalMinutes >= 60 && totalMinutes % 60 === 0) {
            const hours = totalMinutes / 60;
            return `${hours}h`;
        }
        if (totalMinutes >= 60) {
            const hours = Math.floor(totalMinutes / 60);
            const mins = totalMinutes % 60;
            return `${hours}h${mins.toString().padStart(2, "0")}`;
        }
        return `${totalMinutes} min`;
    };

    const formatScheduling = (notif: any): string => {
        const s = notif.scheduling;
        if (!s) return "—";
        if (s.type === "fixed") return `Date fixe: ${formatParisDate(s.date)}`;
        const directionLabel =
            s.type === "before_start" || s.type === "before_end"
                ? "avant"
                : "après";
        const anchorLabel =
            s.type === "before_start" || s.type === "after_start"
                ? "le début"
                : "la fin";
        return `${formatDuration(s.offset, s.unit)} ${directionLabel} ${anchorLabel}`;
    };

    if (!cls) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-7xl rounded-2xl p-0 overflow-hidden">
                {/* Step indicator */}
                <div className="flex items-center gap-0 px-8 pt-12 pb-2">
                    {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => {
                        const stepLabels = mode === "validate"
                            ? ["Récapitulatif", "Emails", "Confirmation"]
                            : ["Récapitulatif", "Emails"];
                        return (
                            <React.Fragment key={s}>
                                <div className="flex items-center gap-2">
                                    <div
                                        className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${step === s
                                            ? "bg-primary text-white shadow-md shadow-primary/20"
                                            : step > s
                                                ? "bg-emerald-100 text-emerald-600"
                                                : "bg-slate-100 text-slate-400"
                                            }`}
                                    >
                                        {step > s ? (
                                            <CheckCircle2 className="h-4 w-4" />
                                        ) : (
                                            s
                                        )}
                                    </div>
                                    <span
                                        className={`text-xs font-semibold hidden sm:inline ${step === s
                                            ? "text-slate-900"
                                            : step > s
                                                ? "text-emerald-600"
                                                : "text-slate-400"
                                            }`}
                                    >
                                        {stepLabels[s - 1]}
                                    </span>
                                </div>
                                {s < totalSteps && (
                                    <div
                                        className={`flex-1 h-0.5 mx-3 rounded-full ${step > s
                                            ? "bg-emerald-200"
                                            : "bg-slate-100"
                                            }`}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Step 1: Class Recap */}
                {step === 1 && (
                    <div className="px-8 pb-6 space-y-5">
                        <DialogHeader>
                            <DialogTitle className="text-lg font-bold flex items-center gap-2">
                                <Video className="h-5 w-5 text-primary" />
                                Récapitulatif de la classe
                            </DialogTitle>
                            <DialogDescription className="text-sm text-slate-500">
                                {mode === "validate"
                                    ? "Vérifiez les détails avant de poursuivre."
                                    : "Détails de la classe virtuelle."}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-3">
                            <DetailRow
                                icon={<Video className="h-4 w-4" />}
                                label="Libellé de la classe"
                                value={cls.name}
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <DetailRow
                                    icon={<Calendar className="h-4 w-4" />}
                                    label="Date et heure de début"
                                    value={formatParisDate(
                                        cls.start_time || cls.startTime
                                    )}
                                />
                                <DetailRow
                                    icon={<Clock className="h-4 w-4" />}
                                    label="Date et heure de fin"
                                    value={formatParisDate(
                                        cls.end_time || cls.endTime
                                    )}
                                />
                            </div>
                            <DetailRow
                                icon={<Mail className="h-4 w-4" />}
                                label="Professeur responsable"
                                value={(cls as any).email || "—"}
                            />
                            {((cls as any).co_professors || []).filter((e: string) => e?.trim()).length > 0 && (
                                <DetailRow
                                    icon={<UserPlus className="h-4 w-4" />}
                                    label="Co-professeurs"
                                    value={((cls as any).co_professors as string[]).filter(e => e?.trim()).join(", ")}
                                />
                            )}
                            <DetailRow
                                icon={<Users className="h-4 w-4" />}
                                label="Source des étudiants"
                                value={
                                    cls.student_source === "csv"
                                        ? `CSV (${cls.csv_students?.length || 0} étudiants)`
                                        : `Learnybox (${Object.keys(
                                            (cls as any)
                                                .formations_config || {}
                                        ).length
                                        } formation(s))`
                                }
                            />
                            <DetailRow
                                icon={<Bell className="h-4 w-4" />}
                                label="Notifications"
                                value={`${notifications.length} email(s) programmé(s)`}
                            />
                            <DetailRow
                                icon={<PenLine className="h-4 w-4" />}
                                label="Signature EduSign"
                                value={
                                    (cls as any).signature_at_start
                                        ? "Signature au début de la classe (avant le cours)"
                                        : "Signature à la fin de la classe (après le cours)"
                                }
                            />
                        </div>

                        <DialogFooter className="pt-2">
                            <Button
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                className="rounded-xl"
                            >
                                Annuler
                            </Button>
                            <Button
                                onClick={() => setStep(2)}
                                className="rounded-xl font-bold"
                            >
                                Suivant
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </DialogFooter>
                    </div>
                )}

                {/* Step 2: Email Preview */}
                {step === 2 && (
                    <div className="px-8 pb-6 space-y-5">
                        <DialogHeader>
                            <DialogTitle className="text-lg font-bold flex items-center gap-2">
                                <Mail className="h-5 w-5 text-primary" />
                                Aperçu des emails
                            </DialogTitle>
                            <DialogDescription className="text-sm text-slate-500">
                                Vérifiez les emails qui seront envoyés.
                            </DialogDescription>
                        </DialogHeader>

                        {previewLoading ? (
                            <div className="flex flex-col items-center justify-center py-12 gap-3">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <p className="text-sm text-slate-500 font-medium">
                                    Chargement de l'aperçu...
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Email previews with tabs */}
                                {previews.length > 0 ? (
                                    <Tabs defaultValue="0" className="w-full">
                                        <TabsList className="w-full justify-start bg-slate-100 rounded-lg p-1">
                                            {previews.map((_, i) => (
                                                <TabsTrigger
                                                    key={i}
                                                    value={String(i)}
                                                    className="rounded-md text-xs font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm"
                                                >
                                                    Email {i + 1}
                                                </TabsTrigger>
                                            ))}
                                        </TabsList>
                                        {previews.map(({ notification, preview }, i) => (
                                            <TabsContent key={notification.id || i} value={String(i)} className="mt-3">
                                                <div className="border border-slate-200 rounded-xl overflow-hidden">
                                                    <div className="bg-slate-50 px-4 py-2.5 flex items-center justify-between border-b border-slate-200">
                                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                            Email {i + 1}
                                                        </span>
                                                        <span className="text-xs font-medium text-slate-400 bg-white px-2 py-0.5 rounded-md border border-slate-100">
                                                            {formatScheduling(notification)}
                                                        </span>
                                                    </div>
                                                    {preview.sample_email ? (
                                                        <div className="p-4 space-y-3">
                                                            <div>
                                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-0.5">
                                                                    Objet
                                                                </p>
                                                                <p className="text-sm font-semibold text-slate-800">
                                                                    {preview.sample_email.subject}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">
                                                                    Aperçu du contenu
                                                                </p>
                                                                {/* Email body is admin-authored template content rendered by backend - same pattern as PreviewModal */}
                                                                <div
                                                                    className="text-sm text-slate-600 bg-white border border-slate-100 rounded-lg p-3 max-h-96 h-96 overflow-y-auto prose prose-sm prose-slate"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: preview.sample_email.body,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 text-sm text-slate-400 italic">
                                                            Aucun aperçu disponible
                                                        </div>
                                                    )}
                                                </div>
                                            </TabsContent>
                                        ))}
                                    </Tabs>
                                ) : (
                                    <div className="text-center py-8 text-sm text-slate-400">
                                        Aucune notification configurée.
                                    </div>
                                )}
                            </div>
                        )}

                        <DialogFooter className="pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setStep(1)}
                                className="rounded-xl"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Retour
                            </Button>
                            {mode === "validate" ? (
                                <Button
                                    onClick={() => setStep(3)}
                                    className="rounded-xl font-bold"
                                    disabled={previewLoading}
                                >
                                    Suivant
                                    <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => onOpenChange(false)}
                                    className="rounded-xl font-bold"
                                >
                                    Fermer
                                </Button>
                            )}
                        </DialogFooter>
                    </div>
                )}

                {/* Step 3: Final Confirmation */}
                {step === 3 && (
                    <div className="px-8 pb-6 space-y-5">
                        <DialogHeader>
                            <DialogTitle className="text-lg font-bold flex items-center gap-2">
                                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                                Confirmation de validation
                            </DialogTitle>
                            <DialogDescription className="text-sm text-slate-500">
                                Cette action est{" "}
                                <strong className="text-red-600">
                                    irréversible
                                </strong>
                                .
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4">
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-amber-800">
                                    <p className="font-bold mb-1">
                                        Attention
                                    </p>
                                    <p>
                                        La classe sera verrouillée et ne pourra
                                        plus être modifiée après validation.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2 py-2">
                                <p className="text-sm font-semibold text-slate-700 mb-3">
                                    Voici ce qui va se passer :
                                </p>
                                <ul className="space-y-2 text-sm text-slate-600">
                                    <ActionItem
                                        icon="✓"
                                        color="text-emerald-500"
                                        text="Création du lien Google Meet"
                                    />
                                    <ActionItem
                                        icon="✓"
                                        color="text-emerald-500"
                                        text="Synchronisation des étudiants"
                                    />
                                    <ActionItem
                                        icon="✓"
                                        color="text-emerald-500"
                                        text={`Planification de ${notifications.length} email(s)`}
                                    />
                                    <ActionItem
                                        icon="✓"
                                        color="text-emerald-500"
                                        text="Création du cours EduSign"
                                    />
                                    <ActionItem
                                        icon="🔒"
                                        color="text-red-500"
                                        text="Verrouillage de la classe"
                                    />
                                </ul>
                            </div>
                        </div>

                        <DialogFooter className="pt-2">
                            <div className="flex w-full justify-between">
                                <Button
                                    variant="outline"
                                    onClick={() => setStep(2)}
                                    disabled={isValidating}
                                    className="rounded-xl"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Retour
                                </Button>
                                <Button
                                    onClick={handleValidate}
                                    disabled={isValidating}
                                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                                >
                                    {isValidating ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                    )}
                                    Valider définitivement
                                </Button>
                            </div>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const DetailRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value: string;
}> = ({ icon, label, value }) => (
    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
        <div className="text-slate-400 mt-0.5">{icon}</div>
        <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                {label}
            </p>
            <p className="text-sm font-semibold text-slate-800 break-words">
                {value}
            </p>
        </div>
    </div>
);

const ActionItem: React.FC<{
    icon: string;
    color: string;
    text: string;
}> = ({ icon, color, text }) => (
    <li className="flex items-start gap-2">
        <span className={`${color} mt-0.5`}>{icon}</span>
        {text}
    </li>
);

export default ValidationWizard;
